import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';

interface PageContent {
  title: string;
  content: string;
  description?: string;
  layout?: 'full' | 'sidebar' | 'narrow';
  section?: 'main' | 'footer' | 'header';
  showTitle?: boolean;
  className?: string;
  published?: boolean;
}

interface StaticPageProps {
  slug: string;
  layout?: PageContent['layout'];
  section?: PageContent['section'];
  showTitle?: boolean;
  className?: string;
}

function parseMdFile(text: string): PageContent {
  const parts = text.split('---');
  if (parts.length < 3) throw new Error('Invalid markdown format');

  const frontMatterLines = parts[1].trim().split('\n');
  const data: Record<string, any> = {};
  let isReadingContent = false;
  let contentLines: string[] = [];
  
  frontMatterLines.forEach(line => {
    if (line.trim().startsWith('content: >-')) {
      isReadingContent = true;
      return;
    }
    
    if (isReadingContent) {
      if (line.trim().startsWith('description:')) {
        isReadingContent = false;
      } else {
        contentLines.push(line.trim());
        return;
      }
    }
    
    if (!isReadingContent) {
      const [key, ...values] = line.split(':');
      if (key && values.length) {
        const value = values.join(':').trim().replace(/^["']|["']$/g, '');
        if (key.trim() !== 'content') {
          data[key.trim()] = value;
        }
      }
    }
  });

  console.log('Content lines:', contentLines);

  return {
    title: data.title || '',
    content: contentLines.join('\n'),
    description: data.description,
    layout: data.layout as PageContent['layout'],
    section: data.section as PageContent['section'],
    showTitle: data.showTitle === 'true',
    published: data.published === 'true'
  };
}

export default function StaticPage({ 
  slug,
  layout = 'narrow',
  section = 'main',
  showTitle = true,
  className = ''
}: StaticPageProps) {
  const [page, setPage] = useState<PageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPage = async () => {
      let responseText = '';
      
      try {
        console.log('Fetching page:', `/content/pages/${slug}.md`);
        const response = await fetch(`/content/pages/${slug}.md`);
        
        if (!response.ok) throw new Error('Pagina non trovata');
        
        responseText = await response.text();
        console.log('Raw markdown:', responseText);
        
        const pageData = parseMdFile(responseText);
        console.log('Page data:', pageData);
        setPage(pageData);
      } catch (err) {
        console.error('Errore dettagliato:', err);
        console.error('Response text:', responseText);
        setError('Impossibile caricare la pagina');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  // Stili per i diversi layout
  const layoutStyles = {
    full: 'w-full max-w-none px-4',
    narrow: 'max-w-2xl mx-auto px-4',
    sidebar: 'max-w-4xl mx-auto px-4 lg:grid lg:grid-cols-3 lg:gap-8'
  };

  // Stili per le diverse sezioni
  const sectionStyles = {
    main: 'py-8',
    footer: 'bg-gray-50 py-12',
    header: 'bg-white shadow-sm'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pizzaveg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => navigate('/')}
          className="text-pizzaveg hover:underline"
        >
          Torna alla home
        </button>
      </div>
    );
  }

  if (!page) return null;

  return (
    <article className={`${sectionStyles[section]} ${className}`}>
      <div className={layoutStyles[layout]}>
        {showTitle && page.title && (
          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            {page.title}
          </h1>
        )}
        
        <div className={`prose prose-lg max-w-none ${
          layout === 'sidebar' ? 'lg:col-span-2' : ''
        }`}>
          <ReactMarkdown>{page.content}</ReactMarkdown>
        </div>

        {layout === 'sidebar' && (
          <aside className="lg:col-span-1 mt-8 lg:mt-0">
            {/* Sidebar content se necessario */}
          </aside>
        )}
      </div>
    </article>
  );
} 